import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import {
    MaintenanceBoxWrapper,
    MaintenanceText,
    MaintenanceButton,
    MaintenancePalm,
    MaintenanceHtml,
} from './MaintenanceBox.styles'
import maintenanceIcon from 'assets/icons/warning_noBackground.svg'
import palmIcon from 'assets/images/maintenance/palm.svg'
import palmIconMobile from 'assets/images/maintenance/palm-mobile.svg'
import { useResolutions } from 'store/resolutions'
import { useEffect, useMemo } from 'react'
import { replaceEmptySpace } from 'helpers/replaceEmptySpace'
import Info from 'components/shared/Info'
import { MaintenanceBoxProps } from './MaintenanceBox.types'

const MaintenanceBox = ({
    variant = 'palm',
    event,
    buttonLabel,
    buttonUrl,
    onOpenHoursModal,
    hasDarkMode,
    ...props
}: MaintenanceBoxProps) => {
    const { isTablet } = useResolutions()

    const selectedOrphants = ['w', 'o', 'do', '90', 'in', 'a']
    const shouldShowButton = buttonLabel && buttonUrl
    const { title, description } = event

    const parsedTitle = useMemo(
        () =>
            title ? replaceEmptySpace(title, selectedOrphants, 'after') : '',
        [title]
    )
    const parsedDescription = useMemo(
        () =>
            description
                ? replaceEmptySpace(description, selectedOrphants, 'after')
                : '',
        [description]
    )

    useEffect(() => {
        // adding action on link included in text returned from the endpoint
        // TODO: rewrite if some better way exists
        const openingHoursLink = document.querySelectorAll('.openingHours')

        openingHoursLink?.forEach((link) =>
            link.addEventListener('click', () => {
                onOpenHoursModal && onOpenHoursModal()
            })
        )
    }, [])

    if (hasDarkMode) {
        return (
            <Info
                color="warning"
                variantType="warning"
                hasDarkMode={hasDarkMode}
                {...(hasDarkMode && {
                    hasNoBackground: true,
                    hasBorder: true,
                    borderColor: 'primary_300',
                })}
            >
                <Box direction="column" ml={[0, 16]} gap={8}>
                    {!!title && (
                        <Text
                            variant="bodyLargeStrong"
                            color={
                                hasDarkMode ? 'grayscale_400' : 'grayscale_900'
                            }
                            dangerouslySetInnerHTML={{ __html: parsedTitle }}
                        />
                    )}
                    <MaintenanceText
                        variant="body"
                        hasDarkMode={hasDarkMode}
                        // TODO: replace globally in project to some proper plugin
                        dangerouslySetInnerHTML={{
                            __html: parsedDescription,
                        }}
                    />
                </Box>
            </Info>
        )
    }

    // TODO: discuss with design team if below can be aligned with the rest of info boxes
    // TODO: dangerouslySetInnerHTML - replace globally in project to some proper plugin/solution (if even possible)

    return variant === 'info' ? (
        // std info version
        <Info
            color="warning"
            backgroundColor="primary_100"
            iconColor="primary_500"
        >
            <MaintenanceHtml
                variant="bodySmall"
                color="grayscale_900"
                dangerouslySetInnerHTML={{
                    __html: parsedDescription,
                }}
                data-testid="ticket-date-info"
            />
        </Info>
    ) : (
        // version with decorations
        <MaintenanceBoxWrapper isFullWidth {...props}>
            <MaintenancePalm
                src={isTablet ? palmIconMobile : palmIcon}
                alt=""
            />
            <Box
                direction={['column', 'row']}
                align="flex-start"
                gap={[8, 0]}
                isFullWidth
            >
                <img src={maintenanceIcon} alt="" />
                <Box direction="column" ml={[0, 16]} gap={8}>
                    {!!title && (
                        <Text
                            variant="bodyLargeStrong"
                            color="grayscale_900"
                            dangerouslySetInnerHTML={{ __html: parsedTitle }}
                        />
                    )}
                    <MaintenanceText
                        variant="body"
                        dangerouslySetInnerHTML={{
                            __html: parsedDescription,
                        }}
                    />
                </Box>
                {!!shouldShowButton && (
                    <MaintenanceButton href={buttonUrl} target="_blank">
                        {buttonLabel}
                    </MaintenanceButton>
                )}
            </Box>
        </MaintenanceBoxWrapper>
    )
}

export default MaintenanceBox
